import React, { useEffect, useState, useCallback, useRef } from "react";
import { Users, Gem, User } from "lucide-react";
import { useAuth } from "../services/AuthContext.js";
import { listUsers } from "../services/user_info.js";
import { Link } from "react-router-dom";
import "./network.css";

const PAGE_SIZE = 21;
const SEARCH_DEBOUNCE_MS = 300;
const default_profile_image_url =
  "https://dominio-eletrico-profile-images-dev.s3.us-east-1.amazonaws.com/default_user_image.jpg";

const PROFILE_OPTIONS = [
  { value: "none", label: "Todos" },
  { value: "has_profile_image", label: "Com foto de perfil" },
  { value: "profiles_complete", label: "Perfis completos" },
];

const ACCOUNT_TYPE_OPTIONS = [
  { value: "all", label: "Todos" },
  { value: "free", label: "Gratuita" },
  { value: "paid", label: "Premium" },
];

const NetworkUserCard = ({ networkUser }) => {
  const [imgSrc, setImgSrc] = useState(networkUser.profile_photo || default_profile_image_url);
  const profileUrl = networkUser.slug ? `/user/${networkUser.slug}` : null;

  return (
    <div className="network-card">
      <div className="network-card-top">
        <div className="network-card-photo">
          <img
            src={imgSrc}
            alt={`Foto de ${networkUser.full_name || networkUser.slug || "usuário"}`}
            className="network-card-img"
            onError={() => setImgSrc(default_profile_image_url)}
          />
        </div>

        <div className="network-card-info">
          <h3 className="network-card-name">
            {profileUrl ? (
              <Link to={profileUrl} className="network-card-link">
                {networkUser.full_name || "—"}
              </Link>
            ) : (
              networkUser.full_name || "—"
            )}
          </h3>
          {networkUser.slug && (
            <p className="network-card-handle">
              <Link to={profileUrl} className="network-card-link">
                @{networkUser.slug}
              </Link>
            </p>
          )}

          {networkUser.account_type === "paid" ? (
            <span className="premium-specific-message network-card-badge">
              <Gem size={14} /> premium
            </span>
          ) : (
            <span className="free-specific-message network-card-badge">
              <User size={14} /> gratuita
            </span>
          )}
        </div>
      </div>

      <div className="network-card-bottom">
        <p className="network-card-bio">
          {networkUser.biography || "(usuário sem biografia cadastrada)"}
        </p>
      </div>
    </div>
  );
};

const Network = () => {
  const { getIdToken } = useAuth();

  const [users, setUsers] = useState([]);
  const [offset, setOffset] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  const [searchInput, setSearchInput] = useState("");
  const [q, setQ] = useState("");
  const [profile, setProfile] = useState("none");
  const [accountType, setAccountType] = useState("all");
  const [sortBy, setSortBy] = useState("score");
  const [sortOrder, setSortOrder] = useState("desc");

  const requestIdRef = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setQ(searchInput.trim());
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchInput]);

  const fetchUsers = useCallback(
    async ({ currentOffset, replace }) => {
      const reqId = ++requestIdRef.current;
      try {
        setLoading(true);
        setError(null);
        const token = await getIdToken();
        const data = await listUsers(token, {
          offset: currentOffset,
          limit: PAGE_SIZE,
          account_type: accountType === "all" ? undefined : accountType,
          profile: profile === "none" ? undefined : profile,
          q: q || undefined,
          sort_by: sortBy,
          sort_order: sortOrder,
        });

        if (reqId !== requestIdRef.current) return;

        const batch = data?.users || [];
        const moreAvailable =
          typeof data?.has_next === "boolean" ? data.has_next : batch.length === PAGE_SIZE;

        if (replace) {
          setUsers(batch);
          setOffset(batch.length);
        } else {
          setUsers((prev) => {
            const existingIds = new Set(prev.map((u) => u.user_id || u.slug));
            const deduped = batch.filter((u) => !existingIds.has(u.user_id || u.slug));
            return [...prev, ...deduped];
          });
          setOffset(currentOffset + batch.length);
        }
        setHasMore(moreAvailable && batch.length > 0);
      } catch (err) {
        if (reqId !== requestIdRef.current) return;
        console.error("Erro ao carregar usuários:", err);
        setError(err.message || "Não foi possível carregar a lista de usuários.");
        if (replace) {
          setUsers([]);
          setOffset(0);
          setHasMore(false);
        }
      } finally {
        if (reqId === requestIdRef.current) setLoading(false);
      }
    },
    [getIdToken, accountType, profile, q, sortBy, sortOrder]
  );

  useEffect(() => {
    setUsers([]);
    setOffset(0);
    setHasMore(true);
    fetchUsers({ currentOffset: 0, replace: true });
  }, [fetchUsers]);

  return (
    <div className="network-container">
      <div className="network-header">
        <h1 className="network-title">
          <Users size={30} className="network-title-icon" />
          Rede de usuários
        </h1>
        <p className="network-subtitle">
          Conheça outros membros da comunidade do Domínio Elétrico.
        </p>
      </div>

      <div className="network-controls">
        <div className="network-control network-control-search">
          <label htmlFor="network-search" className="network-control-label">
            Buscar
          </label>
          <input
            id="network-search"
            type="search"
            className="network-control-input"
            placeholder="Nome ou slug..."
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            autoComplete="off"
          />
        </div>

        <div className="network-control">
          <label htmlFor="network-account-type" className="network-control-label">
            Tipo de conta
          </label>
          <select
            id="network-account-type"
            className="network-control-select"
            value={accountType}
            onChange={(e) => setAccountType(e.target.value)}
          >
            {ACCOUNT_TYPE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="network-control">
          <label htmlFor="network-profile" className="network-control-label">
            Perfil
          </label>
          <select
            id="network-profile"
            className="network-control-select"
            value={profile}
            onChange={(e) => setProfile(e.target.value)}
          >
            {PROFILE_OPTIONS.map((opt) => (
              <option key={opt.value} value={opt.value}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>

        <div className="network-control">
          <label htmlFor="network-sort-by" className="network-control-label">
            Ordenar por
          </label>
          <select
            id="network-sort-by"
            className="network-control-select"
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
          >
            <option value="name">Nome</option>
            <option value="score">Pontuação</option>
          </select>
        </div>

        <div className="network-control">
          <label htmlFor="network-sort-order" className="network-control-label">
            Direção
          </label>
          <select
            id="network-sort-order"
            className="network-control-select"
            value={sortOrder}
            onChange={(e) => setSortOrder(e.target.value)}
          >
            <option value="asc">Crescente</option>
            <option value="desc">Decrescente</option>
          </select>
        </div>
      </div>

      {error && <div className="network-error">{error}</div>}

      <div className="network-grid">
        {users.map((networkUser) => (
          <NetworkUserCard
            key={networkUser.user_id || networkUser.slug}
            networkUser={networkUser}
          />
        ))}
      </div>

      {loading && <div className="network-loading">Carregando usuários...</div>}

      {!loading && users.length === 0 && !error && (
        <div className="network-empty">Nenhum usuário encontrado.</div>
      )}

      {!loading && hasMore && users.length > 0 && (
        <div className="network-load-more-wrapper">
          <button
            type="button"
            className="network-load-more"
            onClick={() => fetchUsers({ currentOffset: offset, replace: false })}
          >
            Carregar mais
          </button>
        </div>
      )}
    </div>
  );
};

export { Network };
